import React, { useState, useEffect } from 'react';
import Header from './components/Header';
import RoomSelection from './components/RoomSelection';
import Booking from './components/Booking';
import { BookingProvider } from './context/BookingContext';
import WidgetLoader from './components/WidgetLoader';
import PaymentSuccessModal from './components/PaymentSuccessModal';
import Member from './components/Member';

function App() {
  const [currentPage, setCurrentPage] = useState('index');
  const [paymentResult, setPaymentResult] = useState(null);

  // Function to extract route from hash
 const getRouteFromHash = () => {
  const hash = window.location.hash;
  if (hash === '#/booking') return 'booking';
  if (hash === '#/member') return 'member'; // Add this line
  return 'index';
};

  useEffect(() => {
    // Check URL for widget parameters AND payment status
    const searchParams = new URLSearchParams(window.location.search);
    const fromWidget = searchParams.get('from_widget');
    const paymentStatus = searchParams.get('payment_status');
    
    console.log('🏠 App mounted. URL params:', {
      fromWidget,
      paymentStatus,
      hotelName: searchParams.get('hotel_name'),
      hasSearch: !!window.location.search,
      hash: window.location.hash,
      pathname: window.location.pathname
    });
    
    // Handle payment callback first
    if (paymentStatus) {
      const result = {
        status: paymentStatus,
        bookingReference: searchParams.get('booking_reference'),
        reservationId: searchParams.get('reservation_id'),
        message: searchParams.get('message')
      };
      
      console.log('💰 Payment callback detected:', result);
      setPaymentResult(result);
      
      // Always go to home page after payment
      setCurrentPage('index');
      
      // Clean URL but preserve widget params
      if (window.history.replaceState) {
        let cleanUrl = '/';
        if (fromWidget) {
          cleanUrl += `?from_widget=${fromWidget}`;
          const hotelName = searchParams.get('hotel_name');
          const hotelId = searchParams.get('hotel_id');
          if (hotelName) cleanUrl += `&hotel_name=${hotelName}`;
          if (hotelId) cleanUrl += `&hotel_id=${hotelId}`;
        }
        window.history.replaceState({}, document.title, cleanUrl);
        console.log('🧹 Cleaned URL after payment');
      }
      
    } else if (fromWidget === 'true') {
      // Widget flow - stay on home page
      setCurrentPage('index');
      
      // Don't clean URL here - WidgetLoader needs these params
      // Just ensure we're on the root path with widget params
      if (window.history.replaceState && window.location.pathname !== '/') {
        const params = new URLSearchParams(window.location.search);
        window.history.replaceState({}, document.title, '/?' + params.toString());
      }
      
    } else {
      // Normal routing - Check multiple sources
      const path = window.location.pathname;
      const hash = window.location.hash;
      
      console.log('Routing check:', { path, hash });
      
      // ADD THIS: Check for member hash first
      if (hash === '#/member') {
        setCurrentPage('member');
        console.log('Route set from hash: member');
      }
      // Priority: Hash > Pathname > Default to index
      else if (hash === '#/booking') {
        setCurrentPage('booking');
        console.log('Route set from hash: booking');
      } else if (path.includes('booking')) {
        setCurrentPage('booking');
        console.log('Route set from pathname: booking');
        
        // If accessing via pathname, convert to hash for static hosting
        if (window.history.replaceState && !hash) {
          const params = window.location.search;
          window.history.replaceState({}, document.title, '/#' + path + params);
          console.log('Converted pathname to hash');
        }
      } else {
        setCurrentPage('index');
        console.log('Route set: index');
      }
    }
  }, []);

  // Handle browser back/forward buttons
  useEffect(() => {
    const handlePopState = () => {
      const route = getRouteFromHash();
      setCurrentPage(route);
      console.log('Popstate: route changed to', route);
    };
    
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  // Handle hash changes
  useEffect(() => {
    const handleHashChange = () => {
      const route = getRouteFromHash();
      setCurrentPage(route);
      console.log('Hashchange: route changed to', route);
    };
    
    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, []);

  // --- ADD THIS NEW USEEFFECT FOR TRACKING ---
  useEffect(() => {
    // 1. Push to GTM dataLayer (Tell your marketing team to listen for 'virtual_page_view')
    if (window.dataLayer) {
      window.dataLayer.push({
        event: 'virtual_page_view',
        page_path: window.location.hash || '/',
        page_title: `Booking Engine - ${currentPage}`
      });
      console.log('📊 Sent virtual page view to GTM:', currentPage);
    }

    // 2. Direct GA4 tracking
    if (typeof window.gtag === 'function') {
      window.gtag('config', 'G-8EGP8LB2DZ', {
        page_path: window.location.hash || '/'
      });
    }
  }, [currentPage]); // <--- This runs every time currentPage changes
  // -------------------------------------------

  // --- ADD THIS FOR CONVERSION TRACKING ---
  useEffect(() => {
    if (paymentResult && paymentResult.status === 'success') {
      
      // 1. Direct Google Ads Conversion Tracking
      if (typeof window.gtag === 'function') {
        window.gtag('event', 'conversion', {
            'send_to': 'AW-11256234874/5XBdCILwgrsYEPqGsvcp',
            'transaction_id': paymentResult.reservationId || paymentResult.bookingReference,
            // Add 'value' and 'currency' here if you have the booking amount in paymentResult
        });
        console.log('🎯 Sent Google Ads Conversion');
      }

     // 2. Push to GTM dataLayer for marketing team
      if (window.dataLayer) {
        window.dataLayer.push({
          event: 'payment_success_page', // <--- Changed to avoid duplicate GA4 purchases
          transaction_id: paymentResult.reservationId || paymentResult.bookingReference,
        });
      }
    }
  }, [paymentResult]);
  // ----------------------------------------

  const navigateTo = (page) => {
  console.log('Navigating to:', page);
  setCurrentPage(page);
  
  const searchParams = new URLSearchParams(window.location.search);
  const searchString = searchParams.toString();
  
  if (page === 'booking') {
    const newUrl = searchString ? `/#/booking?${searchString}` : '/#/booking';
    window.history.pushState({}, '', newUrl);
  } else if (page === 'member') { // Add this block
    const newUrl = searchString ? `/#/member?${searchString}` : '/#/member';
    window.history.pushState({}, '', newUrl);
  } else {
    const newUrl = searchString ? `/?${searchString}` : '/';
    window.history.pushState({}, '', newUrl);
  }
};

  const handleClosePaymentModal = () => {
    setPaymentResult(null);
  };

  return (
    <BookingProvider>
      <WidgetLoader />
      
      <div className="min-h-screen bg-white text-text-primary font-sans">
        <Header />
        {/* <main className="">
          {currentPage === 'index' ? (
            <RoomSelection navigateTo={navigateTo} />
          ) : (
            <Booking navigateTo={navigateTo} />
          )}
        </main> */}

        <main>
          {currentPage === 'index' && <RoomSelection navigateTo={navigateTo} />}
          {currentPage === 'booking' && <Booking navigateTo={navigateTo} />}
          {currentPage === 'member' && <Member navigateTo={navigateTo} />}
        </main>
        
        {/* Payment Success/Failure Modal */}
        {paymentResult && (
          <PaymentSuccessModal 
            result={paymentResult}
            onClose={handleClosePaymentModal}
          />
        )}
      </div>
    </BookingProvider>
  );
}

export default App;